/**
 * UpdateNotification Component
 *
 * Non-blocking banner displayed when a newer EMS version is available.
 * Only shown in instance mode when masterSync detects an update.
 * Fetches from /v1/updates/check and compares against /v1/updates/current.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/base/utils/api';
import { ArrowUpCircle, X } from 'lucide-react';

// ============================================================================
// Types
// ============================================================================

interface UpdateCheckResponse {
  data: {
    updateAvailable: boolean;
    currentVersion: string;
    latestVersion?: string;
    releaseDate?: string;
    channel?: string;
    components?: Record<string, string>;
  };
}

// ============================================================================
// Component
// ============================================================================

export function UpdateNotification() {
  const [dismissed, setDismissed] = useState(false);

  const { data: updateInfo } = useQuery({
    queryKey: ['update-check'],
    queryFn: async () => {
      const res = await apiClient.get<UpdateCheckResponse>('/v1/updates/check');
      return res.data.data;
    },
    staleTime: 30 * 60 * 1000, // 30 minutes
    refetchInterval: 6 * 60 * 60 * 1000, // 6 hours
    retry: false,
  });

  if (!updateInfo?.updateAvailable || dismissed) {
    return null;
  }

  return (
    <div className="mx-6 mb-2 flex items-center justify-between gap-3 px-4 py-2.5 rounded-md border border-[var(--ems-primary)]/30 bg-[var(--ems-primary)]/10 text-sm">
      <div className="flex items-center gap-2">
        <ArrowUpCircle size={16} className="text-[var(--ems-primary)] shrink-0" />
        <span className="text-[var(--grok-text-body)]">
          EMS <strong className="text-[var(--grok-text-primary)]">v{updateInfo.latestVersion}</strong> is available.
          {updateInfo.channel && (
            <span className="text-[var(--grok-text-muted)]"> ({updateInfo.channel} channel)</span>
          )}
        </span>
        <a
          href="/platform/release-management"
          className="text-[var(--ems-primary)] hover:underline font-medium"
        >
          View update
        </a>
      </div>
      <button
        onClick={() => setDismissed(true)}
        className="p-1 text-[var(--grok-text-muted)] hover:text-[var(--grok-text-body)] rounded transition-colors"
        title="Dismiss"
      >
        <X size={14} />
      </button>
    </div>
  );
}

export default UpdateNotification;
